import { baseApi } from './baseApi';
import { toFormData } from './apiUtils';

export const groupApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminGroups: builder.query({
      query: (organizationId) => `/admin/organizations/${organizationId}/groups`,
      providesTags: (result, error, organizationId) =>
        result
          ? [
              ...result.map((g) => ({ type: 'Group', id: g.id })),
              { type: 'Group', id: `ORG-${organizationId}` },
            ]
          : [{ type: 'Group', id: `ORG-${organizationId}` }],
    }),
    createGroup: builder.mutation({
      query: ({ organizationId, tileImageUrl, tileFile, terms, channels, name, broadcast }) => {
        const url = `/admin/organizations/${organizationId}/groups`;
        if (tileFile) {
          return {
            url,
            method: 'POST',
            body: toFormData([
              ['name', name],
              ['tile', tileFile],
              ['terms', terms],
              ['broadcast', broadcast],
              ...channels.map((c) => ['channels', c]),
            ]),
          };
        }
        return {
          url,
          method: 'POST',
          body: {
            name,
            tile_image_url: tileImageUrl,
            terms,
            channels,
            broadcast,
          },
        };
      },
      invalidatesTags: (result, error, { organizationId }) => [
        { type: 'Group', id: `ORG-${organizationId}` },
      ],
    }),
    getGroup: builder.query({
      query: (id) => `/admin/groups/${id}`,
      providesTags: (result, error, id) => [{ type: 'Group', id }],
    }),

    getSystemGroups: builder.query({
      query: () => '/admin/system-groups',
      providesTags: (result) =>
        result
          ? [
              ...result.map((g) => ({ type: 'Group', id: g.id })),
              { type: 'Group', id: 'SYSTEM' },
            ]
          : [{ type: 'Group', id: 'SYSTEM' }],
    }),
    getSystemGroup: builder.query({
      query: (id) => `/admin/system-groups/${id}`,
      providesTags: (result, error, id) => [{ type: 'Group', id }],
    }),
    createSystemGroup: builder.mutation({
      query: ({ name, tileImageUrl, tileFile, terms, channels, broadcast }) => {
        const url = '/admin/system-groups';
        if (tileFile) {
          return {
            url,
            method: 'POST',
            body: toFormData([
              ['name', name],
              ['tile', tileFile],
              ['terms', terms],
              ['broadcast', broadcast],
              ...(channels ?? []).map((c) => ['channels', c]),
            ]),
          };
        }
        return {
          url,
          method: 'POST',
          body: {
            name,
            tile_image_url: tileImageUrl,
            terms,
            channels,
            broadcast,
          },
        };
      },
      invalidatesTags: [{ type: 'Group', id: 'SYSTEM' }],
    }),
    updateSystemGroup: builder.mutation({
      query: ({ id, name, tileImageUrl, tileFile, terms, enabled, channels, broadcast }) => {
        const url = `/admin/system-groups/${id}`;
        if (tileFile) {
          return {
            url,
            method: 'PATCH',
            body: toFormData([
              ['name', name],
              ['tile', tileFile],
              ['terms', terms],
              ['enabled', enabled],
              ['broadcast', broadcast],
              ...(channels ?? []).map((c) => ['channels', c]),
            ]),
          };
        }
        const body = {};
        if (name !== undefined) body.name = name;
        if (tileImageUrl !== undefined) body.tile_image_url = tileImageUrl;
        if (terms !== undefined) body.terms = terms;
        if (enabled !== undefined) body.enabled = enabled;
        if (channels !== undefined) body.channels = channels;
        if (broadcast !== undefined) body.broadcast = broadcast;
        return { url, method: 'PATCH', body };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Group', id },
        { type: 'Group', id: 'SYSTEM' },
      ],
    }),
    deleteSystemGroup: builder.mutation({
      query: (id) => ({ url: `/admin/system-groups/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Group', id: 'SYSTEM' }],
    }),
    updateAdminGroup: builder.mutation({
      query: ({
        id,
        name,
        tileImageUrl,
        tileFile,
        terms,
        enabled,
        channels,
        isSystem,
        geolocationRequired,
        broadcast,
      }) => {
        const url = `/admin/groups/${id}`;
        if (tileFile) {
          return {
            url,
            method: 'PATCH',
            body: toFormData([
              ['name', name],
              ['tile', tileFile],
              ['terms', terms],
              ['enabled', enabled],
              ['geolocation_required', geolocationRequired],
              ['broadcast', broadcast],
              ...(channels ?? []).map((c) => ['channels', c]),
            ]),
          };
        }
        const body = {};
        if (name !== undefined) body.name = name;
        if (tileImageUrl !== undefined) body.tile_image_url = tileImageUrl;
        if (terms !== undefined) body.terms = terms;
        if (enabled !== undefined) body.enabled = enabled;
        if (channels !== undefined) body.channels = channels;
        if (isSystem !== undefined) body.is_system = isSystem;
        if (geolocationRequired !== undefined) body.geolocation_required = geolocationRequired;
        if (broadcast !== undefined) body.broadcast = broadcast;
        return { url, method: 'PATCH', body };
      },
      invalidatesTags: (result, error, { id, organizationId }) => [
        { type: 'Group', id },
        { type: 'Group', id: `ORG-${organizationId}` },
      ],
    }),
    deleteGroup: builder.mutation({
      query: ({ id }) => ({ url: `/admin/groups/${id}`, method: 'DELETE' }),
      invalidatesTags: (result, error, { organizationId }) => [
        { type: 'Group', id: `ORG-${organizationId}` },
      ],
    }),
    getGroupSubscribers: builder.query({
      query: (groupId) => `/admin/groups/${groupId}/subscribers`,
      providesTags: (result, error, groupId) => [{ type: 'Subscriber', id: `GROUP-${groupId}` }],
    }),
    addGroupSubscriber: builder.mutation({
      query: ({ groupId, email, phone, channel }) => ({
        url: `/admin/groups/${groupId}/subscribers`,
        method: 'POST',
        body: { email, phone: phone || undefined, channel },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: 'Subscriber', id: `GROUP-${groupId}` },
      ],
    }),
    notifyGroup: builder.mutation({
      query: ({ groupId, message }) => ({
        url: `/admin/groups/${groupId}/notify`,
        method: 'POST',
        body: { message },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: 'GroupMessage', id: groupId },
        { type: 'GroupMessage', id: 'LIST' },
        { type: 'GroupDeliveries', id: groupId },
      ],
    }),
    getScheduledNotifications: builder.query({
      query: (organizationId) => `/admin/organizations/${organizationId}/scheduled-notifications`,
      providesTags: [{ type: 'ScheduledNotification', id: 'LIST' }],
    }),
    scheduleNotification: builder.mutation({
      query: ({ groupId, message, scheduledAt }) => ({
        url: `/admin/groups/${groupId}/notify/schedule`,
        method: 'POST',
        body: { message, scheduled_at: scheduledAt },
      }),
      invalidatesTags: [{ type: 'ScheduledNotification', id: 'LIST' }],
    }),
    rescheduleNotification: builder.mutation({
      query: ({ id, scheduledAt, message }) => ({
        url: `/admin/scheduled-notifications/${id}`,
        method: 'PATCH',
        body: {
          ...(scheduledAt !== undefined ? { scheduled_at: scheduledAt } : {}),
          ...(message !== undefined ? { message } : {}),
        },
      }),
      invalidatesTags: [{ type: 'ScheduledNotification', id: 'LIST' }],
    }),
    cancelScheduledNotification: builder.mutation({
      query: (id) => ({ url: `/admin/scheduled-notifications/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'ScheduledNotification', id: 'LIST' }],
    }),

    getManagerGroups: builder.query({
      query: () => '/manager/groups',
      providesTags: (result) =>
        result
          ? [
              ...result.map((g) => ({ type: 'Group', id: g.id })),
              { type: 'Group', id: 'LIST' },
            ]
          : [{ type: 'Group', id: 'LIST' }],
    }),
    getManagerGroup: builder.query({
      query: (id) => `/manager/groups/${id}`,
      providesTags: (result, error, id) => [{ type: 'Group', id }],
    }),
    getManagerGroupSubscribers: builder.query({
      query: (groupId) => `/manager/groups/${groupId}/subscribers`,
      providesTags: (result, error, groupId) => [{ type: 'Subscriber', id: `GROUP-${groupId}` }],
    }),
    notifyManagerGroup: builder.mutation({
      query: ({ groupId, message }) => ({
        url: `/manager/groups/${groupId}/notify`,
        method: 'POST',
        body: { message },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: 'GroupMessage', id: groupId },
        { type: 'GroupMessage', id: 'LIST' },
        { type: 'GroupDeliveries', id: groupId },
      ],
    }),
    getManagerGroupMessages: builder.query({
      query: (groupId) => `/manager/groups/${groupId}/messages`,
      providesTags: (result, error, groupId) => [{ type: 'GroupMessage', id: groupId }],
    }),
    deleteManagerGroupMessage: builder.mutation({
      query: ({ groupId, messageId }) => ({
        url: `/manager/groups/${groupId}/messages/${messageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: 'GroupMessage', id: groupId }],
    }),
    getAdminGroupMessages: builder.query({
      query: (groupId) => `/admin/groups/${groupId}/messages`,
      providesTags: (result, error, groupId) => [{ type: 'GroupMessage', id: groupId }],
    }),
    deleteAdminGroupMessage: builder.mutation({
      query: ({ groupId, messageId }) => ({
        url: `/admin/groups/${groupId}/messages/${messageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: 'GroupMessage', id: groupId }],
    }),
    getHostGroupMessages: builder.query({
      query: (groupId) => `/host/groups/${groupId}/messages`,
      providesTags: (result, error, groupId) => [{ type: 'GroupMessage', id: groupId }],
    }),
    deleteHostGroupMessage: builder.mutation({
      query: ({ groupId, messageId }) => ({
        url: `/host/groups/${groupId}/messages/${messageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: 'GroupMessage', id: groupId }],
    }),

    getHostGroups: builder.query({
      query: () => '/host/groups',
      providesTags: (result) =>
        result
          ? [
              ...result.map((g) => ({ type: 'Group', id: g.id })),
              { type: 'Group', id: 'LIST' },
            ]
          : [{ type: 'Group', id: 'LIST' }],
    }),
    getHostGroup: builder.query({
      query: (id) => `/host/groups/${id}`,
      providesTags: (result, error, id) => [{ type: 'Group', id }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAdminGroupsQuery,
  useCreateGroupMutation,
  useGetGroupQuery,
  useGetSystemGroupsQuery,
  useGetSystemGroupQuery,
  useCreateSystemGroupMutation,
  useUpdateSystemGroupMutation,
  useDeleteSystemGroupMutation,
  useUpdateAdminGroupMutation,
  useDeleteGroupMutation,
  useGetGroupSubscribersQuery,
  useAddGroupSubscriberMutation,
  useNotifyGroupMutation,
  useGetScheduledNotificationsQuery,
  useScheduleNotificationMutation,
  useRescheduleNotificationMutation,
  useCancelScheduledNotificationMutation,
  useGetManagerGroupsQuery,
  useGetManagerGroupQuery,
  useGetManagerGroupSubscribersQuery,
  useNotifyManagerGroupMutation,
  useGetManagerGroupMessagesQuery,
  useDeleteManagerGroupMessageMutation,
  useGetAdminGroupMessagesQuery,
  useDeleteAdminGroupMessageMutation,
  useGetHostGroupsQuery,
  useGetHostGroupQuery,
  useGetHostGroupMessagesQuery,
  useDeleteHostGroupMessageMutation,
} = groupApi;
